import { useMemo } from "react";

/**
 * Type for the 4x4 avatar grid pattern
 * Each cell is either 0 (empty) or 1 (filled)
 */
export type AvatarGrid = [
  [number, number, number, number],
  [number, number, number, number],
  [number, number, number, number],
  [number, number, number, number],
];

interface PuzzleAvatarProps {
  grid: AvatarGrid;
  color: string;
  size: number;
}

/**
 * Mapping of Tailwind palette color names (used by AICC) to their hex values.
 * If an incoming color isn't in this map we fall back to the provided string,
 * which allows direct hex strings like `#ff0000` to continue working.
 */
const colorMap: Record<string, string> = {
  "palette-olive": "#7D7B4A",
  "palette-pink": "#FF6FBF",
  "palette-green": "#1FC963",
  "palette-blue": "#478DFF",
  "palette-purple": "#9B8BFF",
  "palette-orange": "#FF9D21",
  "palette-yellow": "#F2D53E",
  "palette-aquamarine": "#61C8BA",
  "palette-leaf": "#B0D448",
  "palette-banana": "#DDDF30",
  "palette-red": "#FB6E6E",
  "neutral-500": "#6B7280",
};

/**
 * Converts a 4x4 grid to SVG path data
 * @param grid 4x4 grid of 0s and 1s
 * @param cellSize Size of each cell in the grid
 * @returns SVG path data string
 */
export const avatarGridToPathD = (
  grid: AvatarGrid,
  cellSize: number,
): string => {
  let d = "";

  grid.forEach((row, rowIndex) => {
    row.forEach((cell, colIndex) => {
      if (cell === 1) {
        const x = colIndex * cellSize;
        const y = rowIndex * cellSize;

        d += `M ${x} ${y} L ${x + cellSize} ${y} L ${x + cellSize} ${
          y + cellSize
        } L ${x} ${y + cellSize} Z `;
      }
    });
  });

  return d;
};

/**
 * PuzzleAvatar component renders an SVG avatar based on a 4x4 grid pattern
 */
export const PuzzleAvatar = ({ grid, color, size }: PuzzleAvatarProps) => {
  // Calculate the size of each cell/square in the SVG
  const cellSize = size / 4;

  // Memoize the path data calculation to avoid recalculating on every render
  const pathD = useMemo(
    () => avatarGridToPathD(grid, cellSize),
    [grid, cellSize],
  );

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ maxWidth: "100%", height: "auto" }}
    >
      {/* Map tailwind palette names to hex, fallback to provided color */}
      <path d={pathD} fill={colorMap[color] ?? color} />
    </svg>
  );
};
